// ============================================================================
// Generic Type Guards
// ============================================================================

/**
 * Type guard to check if a value is a string
 */
export const isString = (value: unknown): value is string =>
  typeof value === 'string';

/**
 * Type guard to check if a value is a number
 */
export const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && !Number.isNaN(value);

/**
 * Type guard to check if a value is a boolean
 */
export const isBoolean = (value: unknown): value is boolean =>
  typeof value === 'boolean';

/**
 * Type guard to check if a value is a function
 */
export const isFunction = (
  value: unknown
): value is (...args: unknown[]) => unknown => typeof value === 'function';

/**
 * Type guard to check if a value is an array
 */
export const isArray = <T>(value: unknown): value is T[] =>
  Array.isArray(value);

/**
 * Type guard to check if a value is a non-null object (excludes arrays and null)
 */
export const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Type guard to check if a value is defined (not undefined)
 */
export const isDefined = <T>(value: T | undefined): value is T =>
  value !== undefined;

/**
 * Type guard to check if a value is non-nullable (not null or undefined)
 */
export const isNonNullable = <T>(value: T | null | undefined): value is T =>
  value !== null && value !== undefined;

/**
 * Type guard to check if an object has a specific property
 */
export const hasProperty = <K extends PropertyKey>(
  obj: unknown,
  key: K
): obj is Record<K, unknown> => isObject(obj) && key in obj;

/**
 * Type guard to check if an object has specific properties
 */
export const hasProperties = <K extends PropertyKey>(
  obj: unknown,
  keys: K[]
): obj is Record<K, unknown> =>
  isObject(obj) && keys.every((key) => key in obj);

// ============================================================================
// Error Type Guards
// ============================================================================

/**
 * Type guard to check if a value is an Error instance
 */
export const isError = (value: unknown): value is Error =>
  value instanceof Error;

/**
 * Type guard to check if a value is an error-like object with a message property
 */
export const isErrorWithMessage = (
  value: unknown
): value is { message: string } =>
  isObject(value) && 'message' in value && typeof value.message === 'string';

/**
 * Type guard to check if a value is an error-like object with message and stack
 */
export const isErrorWithStack = (
  value: unknown
): value is { message: string; stack: string } =>
  isErrorWithMessage(value) &&
  'stack' in value &&
  typeof (value as Record<string, unknown>).stack === 'string';

/**
 * Safely extracts an error message from an unknown error value
 */
export const getErrorMessage = (error: unknown): string => {
  if (isErrorWithMessage(error)) return error.message;
  if (isString(error)) return error;
  return 'An unknown error occurred';
};

/**
 * Safely extracts an error stack from an unknown error value
 */
export const getErrorStack = (error: unknown): string => {
  if (isErrorWithStack(error)) return error.stack;
  if (isError(error) && error.stack) return error.stack;
  return '';
};

// ============================================================================
// Object Utility Functions (Type-Safe Alternatives to `delete (obj as any)`)
// ============================================================================

/**
 * Creates a new object with specified keys omitted.
 * Type-safe alternative to `delete (obj as any).key`
 *
 * @example
 * const original = { a: 1, b: 2, c: 3 };
 * const result = omitFields(original, ['b', 'c']);
 * // result: { a: 1 }
 */
export const omitFields = <T extends object, K extends keyof T>(
  obj: T,
  keys: K[]
): Omit<T, K> => {
  const result = { ...obj };
  keys.forEach((key) => {
    delete result[key];
  });
  return result as Omit<T, K>;
};

/**
 * Creates a new object with only specified keys.
 * Type-safe alternative to manual property picking.
 *
 * @example
 * const original = { a: 1, b: 2, c: 3 };
 * const result = pickFields(original, ['a', 'b']);
 * // result: { a: 1, b: 2 }
 */
export const pickFields = <T extends object, K extends keyof T>(
  obj: T,
  keys: K[]
): Pick<T, K> => {
  const result = {} as Pick<T, K>;
  keys.forEach((key) => {
    if (key in obj) {
      result[key] = obj[key];
    }
  });
  return result;
};

// ============================================================================
// Safe Type Assertion Utilities
// ============================================================================

/**
 * Safely narrows a value to a specific type if it passes the type guard.
 * Returns undefined if the type guard fails.
 *
 * @example
 * const value: unknown = { message: 'test' };
 * const error = narrowType(value, isErrorWithMessage);
 * // error is { message: string } | undefined
 */
export const narrowType = <T>(
  value: unknown,
  guard: (value: unknown) => value is T
): T | undefined => {
  return guard(value) ? value : undefined;
};

/**
 * Safely narrows a value to a specific type or throws an error.
 * Useful when you're certain the type should match.
 *
 * @example
 * const value: unknown = { message: 'test' };
 * const error = assertType(value, isErrorWithMessage, 'Expected error object');
 * // error is { message: string }
 */
export const assertType = <T>(
  value: unknown,
  guard: (value: unknown) => value is T,
  errorMessage?: string
): T => {
  if (guard(value)) return value;
  throw new Error(errorMessage ?? 'Type assertion failed');
};

// ============================================================================
// Array Type Guards
// ============================================================================

/**
 * Type guard to check if all elements in an array satisfy a type guard
 */
export const isArrayOf = <T>(
  value: unknown,
  guard: (item: unknown) => item is T
): value is T[] => Array.isArray(value) && value.every(guard);

/**
 * Filters an array to only include elements that pass a type guard
 *
 * @example
 * const mixed: unknown[] = [1, 'hello', 2, 'world'];
 * const strings = filterByType(mixed, isString);
 * // strings: string[] = ['hello', 'world']
 */
export const filterByType = <T>(
  arr: unknown[],
  guard: (item: unknown) => item is T
): T[] => arr.filter(guard);

// ============================================================================
// Record/Object Type Guards
// ============================================================================

/**
 * Type guard to check if a value is a Record with string keys
 */
export const isStringKeyRecord = (
  value: unknown
): value is Record<string, unknown> =>
  isObject(value) && Object.keys(value).every((key) => typeof key === 'string');

/**
 * Type guard to check if a value is a Record with string keys and specific value type
 */
export const isRecordOf = <T>(
  value: unknown,
  valueGuard: (v: unknown) => v is T
): value is Record<string, T> =>
  isStringKeyRecord(value) && Object.values(value).every(valueGuard);

// ============================================================================
// DOM/Event Type Guards
// ============================================================================

/**
 * Type guard to check if a value is an HTMLElement
 */
export const isHTMLElement = (value: unknown): value is HTMLElement =>
  value instanceof HTMLElement;

/**
 * Type guard to check if a value is an InputEvent target
 */
export const isInputElement = (value: unknown): value is HTMLInputElement =>
  value instanceof HTMLInputElement;

/**
 * Type guard to check if an event target is an HTMLElement
 */
export const isEventTargetElement = (
  target: EventTarget | null
): target is HTMLElement => target instanceof HTMLElement;
